import { motion } from "framer-motion";
import { PiXBold } from "react-icons/pi";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import Proximiio from "proximiio-js-library";
import {
  osName,
  browserName,
  mobileModel,
  mobileVendor,
  deviceType,
} from "react-device-detect";
import { Button } from "./ui/button";
import { FilterItemModel } from "@/models/filterItem.model";

import useMapStore from "@/store/mapStore";
import { t } from "i18next";
import { cn, filterByAmenity, getSortedPOIs } from "@/lib/utils";
import useFeaturesStore from "@/store/featuresStore";

function PoiList() {
  const activeFilter = useMapStore((state) => state.activeFilter);
  const features = useFeaturesStore((state) => state.features);
  const filterCategories = useMapStore((state) => state.filterCategories);
  const currentLang = useMapStore((state) => state.currentLang);
  const floors = useMapStore((state) => state.floors);
  const activeFilterCategory = filterCategories.find((category) =>
    category.items.find((items) => items.id === activeFilter.id),
  );
  const mobileMode = useMapStore((state) => state.mobileMode);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const activeKiosk = useMapStore((state) => state.activeKiosk);
  const appSession = useMapStore((state) => state.appSession);
  const map = useMapStore((state) => state.map);

  const setRouteFinish = useMapStore((state) => state.setRouteFinish);
  const centerToFinish = useMapStore((state) => state.centerToFinish);
  const setActiveFilter = useMapStore((state) => state.setActiveFilter);
  const setIsLoading = useMapStore((state) => state.setIsLoading);

  const pois = getSortedPOIs({ features, currentLang, floors });

  const filteredPois = filterByAmenity(
    pois.filter((feature) => feature.properties.type === "poi"),
    activeFilter.id,
  ).sort((a, b) => {
    if (a.properties.title === b.properties.title) {
      return a.properties.level - b.properties.level;
    } else {
      return a.properties.title?.localeCompare(b.properties.title);
    }
  });

  const onSelectHandle = (featureId: string) => {
    //setIsLoading(true);
    setTimeout(() => {
      const feature = features.find((feature) => feature.id === featureId)!;
      setRouteFinish(feature);
      centerToFinish();

      if (
        feature.properties?.metadata?.prevent_polygon === true ||
        feature.properties?.metadata?.prevent_polygon === "true"
      ) {
        setActiveFilter({} as FilterItemModel);
        setTimeout(() => {
          map.disablePolygons();
        }, 1000);
      }

      //save feature select log
      console.log("LOG: save feature select log");
      const userData = {
        osName,
        browserName,
        mobileModel,
        mobileVendor,
        deviceType:
          kioskMode && !mobileMode && activeKiosk?.name
            ? activeKiosk?.name
            : deviceType,
      };

      new Proximiio.InteractionLogger({
        interactionType: "select",
        targetElementType: "feature",
        targetElementId: feature ? feature.id : undefined,
        targetElementTitle: feature ? feature.properties.title : undefined,
        targetElementAmenity: t(activeFilter.title, { lng: "en" }),
        targetElementAmenityCategory: activeFilterCategory
          ? t(activeFilterCategory?.title, { lng: "en" })
          : undefined,
        kioskId: activeKiosk?.id ? activeKiosk?.id : undefined,
        kioskTitle: activeKiosk?.name ? activeKiosk?.name : undefined,
        metadata: userData,
        language: currentLang,
        session: appSession,
      });
    }, 100);
  };

  const onCloseHandle = () => {
    if (Object.keys(map).length > 0) {
      if (mobileMode) {
        setActiveFilter({} as FilterItemModel);
        map.enablePolygonPreventedIcons();
        setTimeout(() => {
          map.setFeaturesHighlight([]);
        }, 1000);
        return;
      }
      setIsLoading(true);
      setTimeout(() => {
        console.log("close handler");
        setActiveFilter({} as FilterItemModel);
        map.enablePolygonPreventedIcons();
      }, 100);
      setTimeout(() => {
        map.setFeaturesHighlight([]);
      }, 1000);
    }
  };

  return (
    <>
      <motion.div
        key="list"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(
          mobileMode
            ? "absolute top-[calc(100dvh-384px)] z-[101] h-96 w-full rounded-t-2xl bg-white p-6 shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)]"
            : "flex h-full flex-col",
        )}
        style={{
          // @ts-expect-error: custom property
          "--category-color": `hsl(${activeFilterCategory?.color})`,
          "--custom-handle-bg": mobileMode
            ? `var(--primary)`
            : `${activeFilterCategory?.color}`,
        }}
      >
        <div
          className={cn("mb-4 flex items-center px-10", mobileMode && "px-0")}
        >
          <h1 className="flex-1 text-2xl font-bold capitalize">
            {t(activeFilter.title)}
            {activeFilter.listAsClosest && currentLang === "en" ? "s" : ""}
          </h1>
          <Button
            onClick={onCloseHandle}
            size="icon"
            variant="outline"
            className={cn(
              "-mr-2 h-12 w-12 justify-end border-0 bg-transparent text-4xl",
              mobileMode &&
                "mr-0 h-8 w-8 justify-center bg-primary text-lg text-white hover:bg-primary/70",
              activeFilterCategory?.color &&
                !mobileMode &&
                "hover:text-[var(--category-color)]",
              activeFilterCategory?.color &&
                mobileMode &&
                "hover:text-white hover:opacity-70",
            )}
          >
            <PiXBold />
          </Button>
        </div>
        <OverlayScrollbarsComponent
          className={cn(
            "overflow-auto",
            mobileMode ? "max-h-72 px-0 pr-10" : "px-10 pb-5",
          )}
          options={{
            scrollbars: { autoHide: "move", autoHideDelay: 3000 },
            overflow: { x: "hidden" },
          }}
          defer
        >
          <ul>
            {filteredPois.map((feature, idx) => (
              <motion.li
                key={feature.properties.id}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                  delay: 0.1 + idx / 10,
                }}
                className={cn(
                  "group flex cursor-pointer items-center justify-between py-1 font-medium hover:text-primary",
                  activeFilterCategory?.color &&
                    "hover:text-[var(--category-color)]",
                  activeFilter.color && "hover:text-[var(--item-color)]",
                )}
                style={{
                  // @ts-expect-error: custom property
                  "--item-color": `hsl(${activeFilter.color})`,
                }}
                onClick={() => onSelectHandle(feature.id)}
              >
                <span className="flex-1">{feature.properties.title}</span>
                <span className="justify-end text-lg font-bold">
                  {feature.floorName}
                </span>
              </motion.li>
            ))}
          </ul>
        </OverlayScrollbarsComponent>
      </motion.div>
    </>
  );
}

export default PoiList;
